import React, { useEffect, useState } from "react";
import { onAuthStateChanged, signOut } from "firebase/auth";
import { auth } from "../firebase";
import { useNavigate } from "react-router-dom";

const API_BASE =
  process.env.REACT_APP_SELLER_API ||
  "https://gallery.pitics.com";

const ROOT_CATEGORIES = [
  "Personalized",
  "Semi-Personalized",
  "Non Personalized",
];

const MAX_FILE_SIZE = 1 * 1024 * 1024; // 1 MB
const ALLOWED_IMAGE_TYPES = ["image/jpeg", "image/png"];

/*
 * Security note:
 * These are client-side checks for UX and early rejection only.
 * The seller API MUST repeat the same validation server-side before
 * storing/approving any application.
 */
const REGEX = {
  // Indian mobile number: 10 digits, first digit 6-9.
  phone: /^[6-9]\d{9}$/,

  // PAN: 5 letters + 4 digits + 1 letter.
  pan: /^[A-Z]{5}[0-9]{4}[A-Z]$/,

  // Aadhaar: exactly 12 digits. No spaces/hyphens are accepted.
  aadhaar: /^\d{12}$/,

  // GSTIN: 15 chars: 2 digits + 5 letters + 4 digits + 1 letter +
  // 1 alphanumeric + Z + 1 alphanumeric.
  gst: /^[0-9]{2}[A-Z]{5}[0-9]{4}[A-Z][A-Z0-9]Z[A-Z0-9]$/,

  // IFSC: 4 letters + 0 + 6 alphanumeric characters.
  ifsc: /^[A-Z]{4}0[A-Z0-9]{6}$/,

  // Indian PIN: exactly 6 digits, first digit 1-9.
  pincode: /^[1-9][0-9]{5}$/,

  // Common Indian bank account range. Backend should verify with bank/KYC data.
  account: /^[0-9]{9,18}$/,

  // Human/business/address text. Allows normal Indian/English punctuation,
  // but rejects control characters, emojis and arbitrary symbols.
  text: /^[A-Za-z0-9][A-Za-z0-9 .,'&()\/#@:+\-]*$/,
};

const sanitize = {
  name: (v) => v.replace(/[^A-Za-z .'\-]/g, "").replace(/\s{2,}/g, " ").slice(0, 100),
  businessName: (v) =>
    v.replace(/[^A-Za-z0-9 .,'&()\/#@:+\-]/g, "").replace(/\s{2,}/g, " ").slice(0, 150),
  phone: (v) => v.replace(/\D/g, "").slice(0, 10),
  panNumber: (v) => v.replace(/[^A-Za-z0-9]/g, "").toUpperCase().slice(0, 10),
  aadhaarNumber: (v) => v.replace(/\D/g, "").slice(0, 12),
  gstNumber: (v) => v.replace(/[^A-Za-z0-9]/g, "").toUpperCase().slice(0, 15),
  accountNumber: (v) => v.replace(/\D/g, "").slice(0, 18),
  ifscCode: (v) => v.replace(/[^A-Za-z0-9]/g, "").toUpperCase().slice(0, 11),
  pincode: (v) => v.replace(/\D/g, "").slice(0, 6),
  address: (v) =>
    v.replace(/[^A-Za-z0-9 .,'&()\/#@:+\-]/g, "").replace(/\s{2,}/g, " ").slice(0, 150),
};

export default function SellerApply({ onSubmitted }) {
  const navigate = useNavigate();

  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [rejectReason, setRejectReason] = useState("");
  const [fieldErrors, setFieldErrors] = useState({});
  const [existingDocs, setExistingDocs] = useState({});

  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    businessName: "",
    businessType: "individual",
    requestedRootCats: [],
    youtubeShortsRequested: false,
    panNumber: "",
    aadhaarNumber: "",
    gstNumber: "",
    accountNumber: "",
    ifscCode: "",
    address1: "",
    address2: "",
    address3: "",
    address4: "",
    pincode: "",
  });

  const [docs, setDocs] = useState({
    panDoc: null,
    aadhaarDoc: null,
    gstDoc: null,
    bankDoc: null,
    addressDoc: null,
  });

  useEffect(() => {
    const unsub = onAuthStateChanged(auth, async (u) => {
      if (!u) {
        setLoading(false);
        return;
      }

      setUser(u);
      setForm((p) => ({
        ...p,
        name: u.displayName || "",
        email: u.email || "",
      }));

      try {
        const token = await u.getIdToken();

        const meRes = await fetch(`${API_BASE}/api/seller/me`, {
          headers: {
            "X-Firebase-Authorization": token,
          },
        });

        const me = await meRes.json();

        if (me?.isAdmin) {
          navigate("/seller/admin");
          return;
        }

        if (me?.sellerActive) {
          onSubmitted?.();
          return;
        }

        if (me?.requestStatus === "rejected") {
          setSubmitted(false);
          setRejectReason(
            me?.rejectReason ||
              "Application rejected. Please correct documents and reapply."
          );
          setFieldErrors(me?.fieldErrors || {});
          setExistingDocs(me?.uploadedDocs || {});

          if (me?.request) {
            setForm({
              name: me.request?.name || "",
              email: me.request?.email || u.email || "",
              phone: me.request?.phone || "",
              businessName: me.request?.businessName || "",
              businessType: me.request?.businessType || "individual",
              requestedRootCats:
                me.request?.requestedRootCats ||
                me.request?.categories ||
                [],
              youtubeShortsRequested: Boolean(
                me.request?.youtubeShortsRequested
              ),
              panNumber: me.request?.panNumber || "",
              aadhaarNumber: me.request?.aadhaarNumber || "",
              gstNumber: me.request?.gstNumber || "",
              accountNumber: me.request?.accountNumber || "",
              ifscCode: me.request?.ifscCode || "",
              address1: me.request?.address1 || "",
              address2: me.request?.address2 || "",
              address3: me.request?.address3 || "",
              address4: me.request?.address4 || "",
              pincode: me.request?.pincode || "",
            });
          }
        } else if (me?.requestStatus === "pending") {
          setSubmitted(true);
        } else {
          setSubmitted(false);
        }
      } catch (e) {
        console.error("Seller profile load failed:", e);
      } finally {
        setLoading(false);
      }
    });

    return () => unsub();
  }, [navigate, onSubmitted]);

  const change = (key, value) => {
    setForm((p) => ({ ...p, [key]: value }));

    // Clear a server-side error as soon as the user changes that field.
    setFieldErrors((p) => ({ ...p, [key]: "" }));
  };

  const setValidatedField = (key, value) => {
    const fn = sanitize[key];
    change(key, fn ? fn(value) : value);
  };

  const setFile = (key, file) => {
    if (!file) {
      setDocs((p) => ({ ...p, [key]: null }));
      return;
    }

    if (file.size > MAX_FILE_SIZE) {
      alert(`${file.name} exceeds the 1 MB limit.`);
      return;
    }

    if (!ALLOWED_IMAGE_TYPES.includes(file.type)) {
      alert("Only JPG, JPEG and PNG image files are allowed.");
      return;
    }

    setDocs((p) => ({ ...p, [key]: file }));
    setFieldErrors((p) => ({ ...p, [key]: "" }));
  };

  const toggleCategory = (name) => {
    if (!name) return;

    setForm((p) => {
      // FIX: the old component incorrectly read p.categories.
      const current = Array.isArray(p.requestedRootCats)
        ? p.requestedRootCats
        : [];

      const exists = current.includes(name);

      return {
        ...p,
        requestedRootCats: exists
          ? current.filter((x) => x !== name)
          : [...current, name],
      };
    });

    setFieldErrors((p) => ({ ...p, requestedRootCats: "" }));
  };

  const validate = () => {
    const errors = {};
    const f = form;

    if (!f.name.trim()) {
      errors.name = "Full name is required.";
    } else if (!REGEX.text.test(f.name.trim())) {
      errors.name = "Use letters, spaces, apostrophe or hyphen only.";
    }

    if (!f.phone) {
      errors.phone = "Phone number is required.";
    } else if (!REGEX.phone.test(f.phone)) {
      errors.phone = "Enter a valid 10-digit Indian mobile number.";
    }

    if (!f.businessName.trim()) {
      errors.businessName = "Business name is required.";
    } else if (!REGEX.text.test(f.businessName.trim())) {
      errors.businessName = "Business name contains unsupported characters.";
    }

    if (!Array.isArray(f.requestedRootCats) || f.requestedRootCats.length === 0) {
      errors.requestedRootCats = "Select at least one selling root category.";
    }

    // IDs are optional unless your backend makes them mandatory.
    // When supplied, they MUST exactly match the official structure.
    if (f.panNumber && !REGEX.pan.test(f.panNumber)) {
      errors.panNumber = "PAN must be exactly 10 characters: AAAAA9999A.";
    }

    if (f.aadhaarNumber && !REGEX.aadhaar.test(f.aadhaarNumber)) {
      errors.aadhaarNumber = "Aadhaar must contain exactly 12 digits.";
    }

    if (f.gstNumber && !REGEX.gst.test(f.gstNumber)) {
      errors.gstNumber = "GSTIN must be exactly 15 characters in standard format.";
    }

    if (!f.accountNumber) {
      errors.accountNumber = "Bank account number is required.";
    } else if (!REGEX.account.test(f.accountNumber)) {
      errors.accountNumber = "Account number must contain 9-18 digits.";
    }

    if (!f.ifscCode) {
      errors.ifscCode = "IFSC code is required.";
    } else if (!REGEX.ifsc.test(f.ifscCode)) {
      errors.ifscCode = "IFSC must be 11 characters in standard format.";
    }

    const addressFields = ["address1", "address2", "address3", "address4"];
    const enteredAddress = addressFields.some((key) => f[key].trim());

    if (!enteredAddress) {
      errors.address1 = "At least one address line is required.";
    }

    for (const key of addressFields) {
      if (f[key] && !REGEX.text.test(f[key].trim())) {
        errors[key] = "Address contains unsupported characters.";
      }
    }

    if (!f.pincode) {
      errors.pincode = "Pincode is required.";
    } else if (!REGEX.pincode.test(f.pincode)) {
      errors.pincode = "Pincode must be exactly 6 digits.";
    }

    // If a verification number was supplied, its corresponding proof
    // should also be supplied unless an existing approved/reviewed document exists.
    const documentRules = [
      ["panNumber", "panDoc", "PAN proof"],
      ["aadhaarNumber", "aadhaarDoc", "Aadhaar proof"],
      ["gstNumber", "gstDoc", "GST proof"],
    ];

    for (const [numberKey, docKey, label] of documentRules) {
      if (f[numberKey] && !docs[docKey] && !existingDocs[`${docKey}Path`]) {
        errors[docKey] = `${label} is required when the number is provided.`;
      }
    }

    if (!docs.bankDoc && !existingDocs.bankDocPath) {
      errors.bankDoc = "Bank proof is required.";
    }

    if (!docs.addressDoc && !existingDocs.addressDocPath) {
      errors.addressDoc = "Address proof is required.";
    }

    setFieldErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const submitRequest = async () => {
    if (!validate()) {
      // Bring the first invalid field into view when possible.
      setTimeout(() => {
        const first = Object.keys(fieldErrors)[0];
        if (first) {
          document.querySelector(`[data-field="${first}"]`)?.scrollIntoView({
            behavior: "smooth",
            block: "center",
          });
        }
      }, 0);
      return;
    }

    try {
      setSubmitting(true);
      setRejectReason("");

      const currentUser = auth.currentUser;
      if (!currentUser) throw new Error("Please login again.");

      const token = await currentUser.getIdToken();
      const body = new FormData();

      Object.keys(form).forEach((k) => {
        if (k === "requestedRootCats") {
          body.append("requestedRootCats", JSON.stringify(form.requestedRootCats));
          // Legacy compatibility.
          body.append("categories", JSON.stringify(form.requestedRootCats));
        } else if (k === "youtubeShortsRequested") {
          body.append("youtubeShortsRequested", String(Boolean(form[k])));
        } else {
          body.append(k, String(form[k] ?? "").trim());
        }
      });

      body.append(
        "fullAddress",
        [
          form.address1,
          form.address2,
          form.address3,
          form.address4,
          form.pincode,
        ]
          .filter(Boolean)
          .join(", ")
      );

      Object.keys(docs).forEach((k) => {
        if (docs[k]) body.append(k, docs[k]);
      });

      const res = await fetch(`${API_BASE}/api/seller/apply`, {
        method: "POST",
        headers: {
          "X-Firebase-Authorization": token,
        },
        body,
      });

      let data = {};
      try {
        data = await res.json();
      } catch {
        throw new Error(`Server returned HTTP ${res.status}.`);
      }

      if (!res.ok || !data.success) {
        if (data?.fieldErrors) {
          setFieldErrors(data.fieldErrors);
        }
        throw new Error(data.error || `Request failed with HTTP ${res.status}.`);
      }

      setSubmitted(true);
      setFieldErrors({});
      setExistingDocs(data.uploadedDocs || existingDocs);
      onSubmitted?.();
    } catch (e) {
      console.error("Seller application failed:", e);
      alert(e?.message || "Failed to submit seller application.");
    } finally {
      setSubmitting(false);
    }
  };

  const UserBadge = () => {
    if (!user) return null;

    return (
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: 10,
          padding: "8px 12px",
          borderRadius: 999,
          background: "rgba(255,255,255,.95)",
          boxShadow: "0 14px 30px rgba(0,0,0,.12)",
        }}
      >
        <img
          src={user.photoURL || "https://via.placeholder.com/40"}
          alt="user"
          style={{
            width: 42,
            height: 42,
            borderRadius: "50%",
            objectFit: "cover",
          }}
        />
        <div>
          <div style={{ fontSize: 13, fontWeight: 900 }}>
            {user.displayName || "Seller"}
          </div>
          <button
            onClick={() => signOut(auth)}
            style={{
              border: "none",
              background: "none",
              color: "#dc2626",
              fontSize: 12,
              cursor: "pointer",
              padding: 0,
            }}
          >
            Sign Out
          </button>
        </div>
      </div>
    );
  };

  const ErrorText = ({ name }) =>
    fieldErrors[name] ? (
      <div
        style={{
          color: "#dc2626",
          fontSize: 12,
          fontWeight: 800,
          marginTop: -8,
          marginBottom: 10,
        }}
      >
        ❌ {fieldErrors[name]}
      </div>
    ) : null;

  const ExistingDoc = ({ name }) => {
    const newFile = docs[name];
    const oldFile = existingDocs[`${name}Path`];

    if (!newFile && !oldFile) return null;

    return (
      <div
        style={{
          fontSize: 12,
          marginTop: 8,
          fontWeight: 800,
          wordBreak: "break-word",
          lineHeight: 1.5,
        }}
      >
        {oldFile && !newFile && (
          <div style={{ color: "#16a34a" }}>
            Current File:
            <br />
            {oldFile}
          </div>
        )}

        {newFile && (
          <div style={{ color: "#2563eb" }}>
            Selected File:
            <br />
            {newFile.name}
          </div>
        )}
      </div>
    );
  };

  const page = {
    minHeight: "100vh",
    padding: 24,
    background:
      "radial-gradient(circle at top left,#60a5fa,#7c3aed,#111827)",
    fontFamily: "Inter, Arial",
  };

  const shell = {
    maxWidth: 1320,
    margin: "0 auto",
  };

  const hero = { color: "#fff" };

  const card = {
    background: "rgba(255,255,255,.95)",
    borderRadius: 34,
    padding: 28,
    boxShadow: "0 40px 90px rgba(0,0,0,.25)",
  };

  const grid = {
    display: "grid",
    gridTemplateColumns: "minmax(0,1fr) 8px minmax(0,1fr)",
    gap: 26,
  };

  const divider = {
    borderRadius: 999,
    background:
      "linear-gradient(to bottom,#2563eb,#7c3aed,#ec4899,#22c55e)",
  };

  const section = {
    background: "#fff",
    borderRadius: 28,
    padding: 22,
  };

  const input = {
    width: "100%",
    boxSizing: "border-box",
    padding: "15px 16px",
    borderRadius: 16,
    border: "1px solid #dbeafe",
    marginBottom: 14,
    outline: "none",
  };

  const row = {
    display: "grid",
    gridTemplateColumns: "minmax(0,1fr) 190px",
    gap: 14,
    marginBottom: 14,
  };

  const upload = {
    border: "2px dashed #8b5cf6",
    borderRadius: 16,
    padding: 12,
    textAlign: "center",
    cursor: "pointer",
    background: "#faf5ff",
    fontWeight: 800,
    fontSize: 12,
  };

  const btn = {
    border: "none",
    color: "#fff",
    cursor: "pointer",
    padding: "18px 36px",
    borderRadius: 999,
    fontWeight: 900,
    fontSize: 16,
    background: "linear-gradient(135deg,#2563eb,#7c3aed,#ec4899)",
    opacity: submitting ? 0.65 : 1,
  };

  if (loading) {
    return <div className="seller-portal-page seller-application-page" style={page}>Loading...</div>;
  }

  if (!user) {
    return <div className="seller-portal-page seller-application-page" style={page}>Please Login</div>;
  }

  if (submitted) {
    return (
      <div className="seller-portal-page seller-application-page" style={page}>
        <div style={shell}>
          <div style={{ display: "flex", justifyContent: "flex-end", marginBottom: 14 }}>
            <UserBadge />
          </div>
          <div style={card}>
            <h1>✅ Application Submitted</h1>
            <p>Your seller request is under review.</p>
          </div>
        </div>
      </div>
    );
  }

  const identityRows = [
    ["PAN Number", "panNumber", "Upload PAN", "panDoc"],
    ["Aadhaar Number", "aadhaarNumber", "Upload Aadhaar", "aadhaarDoc"],
    ["GST Number", "gstNumber", "Upload GST", "gstDoc"],
    ["Account Number", "accountNumber", "Upload Bank", "bankDoc"],
  ];

  return (
    <div className="seller-portal-page seller-application-page" style={page}>
      <div style={shell}>
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "flex-start",
            gap: 16,
            flexWrap: "wrap",
            marginBottom: 24,
          }}
        >
          <div style={hero}>
            <h1 style={{ fontSize: 42, fontWeight: 900, margin: 0 }}>
              Become PITICS Seller
            </h1>
            <div style={{ marginTop: 10 }}>
              Professional onboarding • Multi-category selling • Secure verification
            </div>
          </div>
          <UserBadge />
        </div>

        {rejectReason && (
          <div
            style={{
              background: "#fef2f2",
              color: "#b91c1c",
              padding: 16,
              borderRadius: 16,
              marginBottom: 18,
              fontWeight: 800,
            }}
          >
            ❌ {rejectReason}
          </div>
        )}

        <div style={card}>
          <div style={grid}>
            <div style={section}>
              <div data-field="name">
                <input
                  style={input}
                  placeholder="Full Name"
                  value={form.name}
                  maxLength={100}
                  autoComplete="name"
                  onChange={(e) => setValidatedField("name", e.target.value)}
                />
                <ErrorText name="name" />
              </div>

              <input
                style={{ ...input, background: "#f8fafc", color: "#64748b" }}
                value={form.email}
                disabled
              />

              <div data-field="phone">
                <input
                  style={input}
                  placeholder="Phone Number"
                  value={form.phone}
                  inputMode="numeric"
                  autoComplete="tel"
                  maxLength={10}
                  onChange={(e) => setValidatedField("phone", e.target.value)}
                />
                <ErrorText name="phone" />
              </div>

              <div data-field="businessName">
                <input
                  style={input}
                  placeholder="Business Name"
                  value={form.businessName}
                  maxLength={150}
                  autoComplete="organization"
                  onChange={(e) =>
                    setValidatedField("businessName", e.target.value)
                  }
                />
                <ErrorText name="businessName" />
              </div>

              <select
                style={input}
                value={form.businessType}
                onChange={(e) => change("businessType", e.target.value)}
              >
                <option value="individual">Individual</option>
                <option value="company">Company</option>
              </select>

              <div
                style={{
                  marginBottom: 18,
                  padding: 16,
                  borderRadius: 18,
                  background: "#f8fafc",
                  border: "1px solid #e2e8f0",
                }}
              >
                <h3 style={{ marginTop: 0 }}>Selling Categories</h3>
                <p style={{ fontSize: 13, color: "#64748b" }}>
                  Select the PITICS categories you want to sell in. Admin will
                  approve access after document verification.
                </p>

                {ROOT_CATEGORIES.map((root) => {
                  const active = form.requestedRootCats.includes(root);
                  return (
                    <label
                      key={root}
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: 10,
                        marginBottom: 10,
                        fontWeight: 800,
                        cursor: "pointer",
                      }}
                    >
                      <input
                        type="checkbox"
                        checked={active}
                        onChange={() => toggleCategory(root)}
                      />
                      {root}
                    </label>
                  );
                })}
                <ErrorText name="requestedRootCats" />
              </div>

              <div
                style={{
                  marginBottom: 18,
                  padding: 16,
                  borderRadius: 18,
                  background: "#fff7ed",
                  border: "1px solid #fed7aa",
                }}
              >
                <h3 style={{ marginTop: 0 }}>YouTube Video Demo Access</h3>
                <p style={{ fontSize: 13, color: "#9a3412", marginTop: 0 }}>
                  Would you like PITICS to allow you to upload YouTube Shorts
                  as product demo videos? Admin will decide access after
                  verification.
                </p>
                <label
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 10,
                    fontWeight: 800,
                    cursor: "pointer",
                  }}
                >
                  <input
                    type="checkbox"
                    checked={Boolean(form.youtubeShortsRequested)}
                    onChange={(e) =>
                      change("youtubeShortsRequested", e.target.checked)
                    }
                  />
                  I am willing to upload my YouTube Shorts for my products promotions.
                </label>
              </div>
            </div>

            <div style={divider} />

            <div style={section}>
              {identityRows.map(([label, key, uploadLabel, docKey]) => (
                <div key={key} style={row} data-field={key}>
                  <div>
                    <input
                      style={{ ...input, marginBottom: 8 }}
                      placeholder={label}
                      value={form[key]}
                      inputMode={
                        key === "accountNumber" ? "numeric" : "text"
                      }
                      maxLength={
                        key === "panNumber"
                          ? 10
                          : key === "aadhaarNumber"
                          ? 12
                          : key === "gstNumber"
                          ? 15
                          : 18
                      }
                      onChange={(e) => setValidatedField(key, e.target.value)}
                    />
                    <ErrorText name={key} />
                  </div>

                  <label style={upload}>
                    {uploadLabel}
                    <input
                      hidden
                      type="file"
                      accept=".jpg,.jpeg,.png"
                      onChange={(e) =>
                        setFile(docKey, e.target.files?.[0])
                      }
                    />
                    <ExistingDoc name={docKey} />
                    <ErrorText name={docKey} />
                  </label>
                </div>
              ))}

              <div data-field="ifscCode">
                <input
                  style={input}
                  placeholder="IFSC Code"
                  value={form.ifscCode}
                  maxLength={11}
                  autoComplete="off"
                  onChange={(e) =>
                    setValidatedField("ifscCode", e.target.value)
                  }
                />
                <ErrorText name="ifscCode" />
              </div>

              {["address1", "address2", "address3", "address4"].map(
                (key, i) => (
                  <div key={key} data-field={key}>
                    <input
                      style={input}
                      placeholder={`Address Line ${i + 1}`}
                      value={form[key]}
                      maxLength={150}
                      autoComplete={i === 0 ? "street-address" : "address-line2"}
                      onChange={(e) => setValidatedField(key, e.target.value)}
                    />
                    <ErrorText name={key} />
                  </div>
                )
              )}

              <div data-field="pincode">
                <input
                  style={input}
                  placeholder="Pincode"
                  value={form.pincode}
                  inputMode="numeric"
                  maxLength={6}
                  autoComplete="postal-code"
                  onChange={(e) => setValidatedField("pincode", e.target.value)}
                />
                <ErrorText name="pincode" />
              </div>

              <div style={row} data-field="addressDoc">
                <input
                  style={{ ...input, marginBottom: 0, background: "#f8fafc" }}
                  value="Address Proof"
                  disabled
                />

                <label style={upload}>
                  Upload Address
                  <input
                    hidden
                    type="file"
                    accept=".jpg,.jpeg,.png"
                    onChange={(e) =>
                      setFile("addressDoc", e.target.files?.[0])
                    }
                  />
                  <ExistingDoc name="addressDoc" />
                  <ErrorText name="addressDoc" />
                </label>
              </div>
            </div>
          </div>

          <div style={{ textAlign: "center", marginTop: 28 }}>
            <button
              style={btn}
              disabled={submitting}
              onClick={submitRequest}
            >
              {submitting
                ? "Submitting..."
                : rejectReason
                ? "Upload Corrected Docs & Reapply"
                : "Create Seller Account"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
